import type { MenuProps } from 'antd'
import { Dropdown, Tooltip } from 'antd'
import { ProposalCard } from 'features/chatbot/components/conversationMessage/ProposalCard'
import {
  ChevronIcon,
  CollapsibleContainer,
  CollapsibleContent,
  CollapsibleHeader,
  OpenInNewTabButton,
  openTooltipStyle,
  ReferenceContent,
  ReferenceItem,
  ReferenceTitle,
  TabColorDot,
  ThoughtFeedback,
  ThoughtItemContainer,
  ThoughtText,
} from 'features/chatbot/components/conversationMessage/styles'
import { FeedbackMenu } from 'features/chatbot/components/FeedbackMenu'
import { useChatbotAttachmentsOptional } from 'features/chatbot/contexts/ChatbotAttachmentContext'
import { buildThoughtFeedbackKey } from 'features/chatbot/contexts/ChatbotFeedbackContext'
import { useInstrumentReferenceFallback } from 'features/chatbot/hooks/useInstrumentReferenceFallback'
import { Source, ThinkingNote } from 'features/chatbot/types/conversation'
import {
  createDocumentTab,
  createTimeSeriesTab,
  TimeSeriesHistoryItem,
  useKnowledgeDocumentUXContextOptional,
  useRolodexEntriesCachedById,
} from 'features/knowledgeDocumentViewer'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { ChevronDown, FileText, LineChart, Plus } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

const TOOLTIP_BG = '#1e1e1e'
const TAB_INDICATOR_COLORS = ['#A8C8F0', '#F0E0A0', '#A8D8B0', '#C8B0E8']
const DOCUMENT_ID_KEYS = ['id', 'document_id', 'documentId'] as const

interface InstrumentWithData {
  instrumentName: string
  entryId: string
  startDatetime?: string
  endDatetime?: string
}

interface AIResponseExtrasProps {
  sources?: Source[]
  instrumentsWithData?: InstrumentWithData[]
  thinkingTimeSeconds?: number
  thinkingNotes?: ThinkingNote[]
  messageId?: string
  messageIndex: number
}

type UnifiedReference =
  | { type: 'document'; source: Source }
  | {
      type: 'instrument'
      label: string
      entryIds: string[]
      startDatetime?: string
      endDatetime?: string
    }

function splitSourcesByType(sources?: Source[]): {
  documents: Source[]
  instruments: Source[]
} {
  if (!sources || sources.length === 0)
    return { documents: [], instruments: [] }
  const seenNames = new Set<string>()
  const documents: Source[] = []
  const instruments: Source[] = []
  for (const source of sources) {
    if (seenNames.has(source.text)) continue
    seenNames.add(source.text)
    if (source.id.startsWith('inst-')) {
      instruments.push(source)
    } else {
      documents.push(source)
    }
  }
  return { documents, instruments }
}

function formatInstrumentGroupLabel(names: string[]): string {
  if (names.length === 1) return names[0]
  if (names.length === 2) return names.join(', ')
  return `${names[0]} +${names.length - 1} more`
}

function collectInstrumentData(items: InstrumentWithData[]): {
  entryIds: string[]
  names: string[]
  startDatetime?: string
  endDatetime?: string
} {
  const entryIds: string[] = []
  const seenNames = new Set<string>()
  const names: string[] = []
  let startDatetime: string | undefined
  let endDatetime: string | undefined

  for (const inst of items) {
    entryIds.push(inst.entryId)
    if (!seenNames.has(inst.instrumentName)) {
      seenNames.add(inst.instrumentName)
      names.push(inst.instrumentName)
    }
    if (
      inst.startDatetime &&
      (!startDatetime || inst.startDatetime < startDatetime)
    )
      startDatetime = inst.startDatetime
    if (inst.endDatetime && (!endDatetime || inst.endDatetime > endDatetime))
      endDatetime = inst.endDatetime
  }

  return { entryIds, names, startDatetime, endDatetime }
}

function resolveDocumentId(source: Source): string {
  for (const key of DOCUMENT_ID_KEYS) {
    const val = source.data[key]
    if (typeof val === 'string' && val) return val
  }
  return source.id
}

function formatThinkingTime(seconds: number): string {
  if (seconds < 60) {
    return `${seconds}s`
  }
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  if (remainingSeconds === 0) {
    return `${minutes}m`
  }
  return `${minutes}m ${remainingSeconds}s`
}

export const AIResponseExtras: React.FC<AIResponseExtrasProps> = ({
  sources,
  instrumentsWithData,
  thinkingTimeSeconds,
  thinkingNotes,
  messageId,
  messageIndex,
}) => {
  const [isThoughtsOpen, setIsThoughtsOpen] = useState(false)
  const [isReferencesOpen, setIsReferencesOpen] = useState(false)
  const documentUXContext = useKnowledgeDocumentUXContextOptional()
  const chatbotAttachments = useChatbotAttachmentsOptional()

  const sourceDocumentIds = useMemo(
    () =>
      (sources ?? [])
        .map((s) => resolveDocumentId(s))
        .filter(
          (id) => id.startsWith('DOC-') || id.startsWith('system-document')
        ),
    [sources]
  )

  const { data: resolvedDocuments } =
    useRolodexEntriesCachedById<SystemDocumentEntry>(sourceDocumentIds)

  const documentTitleMap = useMemo(() => {
    const map = new Map<string, string>()
    if (!resolvedDocuments) return map
    for (const entry of resolvedDocuments) {
      const title =
        entry.entry.data.document_title || entry.entry.data.drawing_number || ''
      if (title) map.set(entry.entry.id, title)
    }
    return map
  }, [resolvedDocuments])

  const unifiedReferences: UnifiedReference[] = useMemo(() => {
    const refs: UnifiedReference[] = []
    const seenNames = new Set<string>()

    if (sources) {
      for (const source of sources) {
        const resolvedTitle =
          documentTitleMap.get(resolveDocumentId(source)) || source.text
        const displayText = resolvedTitle || source.id
        if (!seenNames.has(displayText)) {
          seenNames.add(displayText)
          refs.push({
            type: 'document',
            source: { ...source, text: displayText },
          })
        }
      }
    }

    if (instrumentsWithData && instrumentsWithData.length > 0) {
      const { entryIds, names, startDatetime, endDatetime } =
        collectInstrumentData(instrumentsWithData)
      refs.push({
        type: 'instrument',
        label: formatInstrumentGroupLabel(names),
        entryIds,
        startDatetime,
        endDatetime,
      })
    }

    return refs
  }, [sources, instrumentsWithData, documentTitleMap])

  const existingDashboardTabs = useMemo(() => {
    if (!documentUXContext) return []
    return documentUXContext.openTabs
      .filter((tab) => {
        if (!tab.id.startsWith('timeseries-tab-')) return false
        // Saved-chart tabs render from the resolved chart config and ignore
        // sensor-id seeding, so they are not valid "add sensor here" targets.
        const currentItem = tab.history[tab.currentIndex]
        return !(currentItem.type === 'timeseries' && currentItem.chartID)
      })
      .map((tab) => {
        const currentItem = tab.history[tab.currentIndex]
        const label =
          tab.customLabel ||
          (currentItem.type === 'timeseries'
            ? (currentItem as TimeSeriesHistoryItem).label
            : 'Dashboard')
        return { id: tab.id, label }
      })
  }, [documentUXContext])

  const referenceTabMap = useMemo(() => {
    if (!documentUXContext)
      return new Map<string, { tabId: string; color: string }>()

    const map = new Map<string, { tabId: string; color: string }>()
    const tabs = documentUXContext.openTabs

    for (let tabIndex = 0; tabIndex < tabs.length; tabIndex++) {
      const tab = tabs[tabIndex]
      const currentItem = tab.history[tab.currentIndex]
      const color = TAB_INDICATOR_COLORS[tabIndex % TAB_INDICATOR_COLORS.length]

      if (currentItem.type === 'document') {
        const tabDocId = currentItem.documentId
        if (tabDocId) {
          for (const ref of unifiedReferences) {
            if (ref.type !== 'document') continue
            if (resolveDocumentId(ref.source) === tabDocId) {
              map.set(`doc-${ref.source.id}`, { tabId: tab.id, color })
            }
          }
        }
      } else if (currentItem.type === 'timeseries') {
        const tabSensorIds = new Set(currentItem.sensorIds)
        for (const ref of unifiedReferences) {
          if (ref.type !== 'instrument') continue
          const hasOverlap = ref.entryIds.some((id) => tabSensorIds.has(id))
          if (hasOverlap) {
            map.set('inst-group', { tabId: tab.id, color })
          }
        }
      }
    }

    return map
  }, [documentUXContext, unifiedReferences])

  useEffect(() => {
    if (!documentUXContext) return
    const colorMap = new Map<string, string>()
    for (const { tabId, color } of referenceTabMap.values()) {
      colorMap.set(tabId, color)
    }
    documentUXContext.setTabIndicatorColors(colorMap)
    return () => {
      documentUXContext.setTabIndicatorColors(new Map())
    }
  }, [documentUXContext, referenceTabMap])

  const openInstrumentsFallback = useInstrumentReferenceFallback()

  const handleOpenInstrumentInNewTab = useCallback(
    (entryIds: string[], startDatetime?: string, endDatetime?: string) => {
      let timeWindow: { from: string; to: string } | undefined
      if (startDatetime && endDatetime) {
        timeWindow = { from: startDatetime, to: endDatetime }
      } else {
        const timeWindowAttachment = chatbotAttachments?.attachments.find(
          (a) => a.type === 'timeWindow'
        )
        timeWindow = timeWindowAttachment?.timeWindow
      }

      if (!documentUXContext) {
        openInstrumentsFallback(entryIds, timeWindow)
        return
      }

      const label = `Dashboard ${existingDashboardTabs.length + 1}`
      const newTab = createTimeSeriesTab(entryIds, label, timeWindow)
      documentUXContext.addTab(newTab)
    },
    [
      documentUXContext,
      chatbotAttachments,
      existingDashboardTabs.length,
      openInstrumentsFallback,
    ]
  )

  const handleAddToExistingDashboard = useCallback(
    (tabId: string, entryIds: string[]) => {
      if (!documentUXContext) return
      documentUXContext.addSensorsToTimeSeriesTab(tabId, entryIds)
      documentUXContext.setActiveTabId(tabId)
    },
    [documentUXContext]
  )

  const handleOpenDocumentInNewTab = useCallback(
    (source: Source) => {
      if (!documentUXContext) return

      const newTab = createDocumentTab(resolveDocumentId(source))
      documentUXContext.addTab(newTab)
    },
    [documentUXContext]
  )

  const buildInstrumentDropdownMenu = useCallback(
    (
      entryIds: string[],
      startDatetime?: string,
      endDatetime?: string
    ): MenuProps['items'] => {
      const items: MenuProps['items'] = existingDashboardTabs.map((tab) => ({
        key: `dashboard-${tab.id}`,
        icon: <LineChart size={12} />,
        label: tab.label,
        onClick: () => handleAddToExistingDashboard(tab.id, entryIds),
      }))
      items.push(
        { type: 'divider' },
        {
          key: 'new-dashboard',
          icon: <Plus size={12} />,
          label: 'New dashboard',
          onClick: () =>
            handleOpenInstrumentInNewTab(entryIds, startDatetime, endDatetime),
        }
      )
      return items
    },
    [
      existingDashboardTabs,
      handleAddToExistingDashboard,
      handleOpenInstrumentInNewTab,
    ]
  )

  const hasThoughts = thinkingNotes && thinkingNotes.length > 0
  const hasReferences = unifiedReferences.length > 0

  // Pull out proposal-tool calls so we render them as full Accept/Reject
  // cards above the thoughts collapsible. Each proposal_id maps to a
  // single card; if the agent calls the tool more than once in a turn,
  // each gets its own card.
  const proposalIds = useMemo(() => {
    if (!thinkingNotes) return [] as string[]
    const ids: string[] = []
    for (const note of thinkingNotes) {
      if (note.toolCall?.name !== 'propose_case_update') continue
      const args = note.toolCall.args as { proposal_id?: unknown } | undefined
      // The tool's *args* describe the proposal; the *result* carries
      // the proposal_id. Live SSE captures only args (we don't yet
      // surface the result payload through thoughts), so we fall back
      // to a synthetic key based on field_path + proposed_value if
      // proposal_id isn't there. Persisted runs have proposal_id from
      // the tool result via the streaming pipeline.
      const id =
        typeof args === 'object' &&
        args !== null &&
        'proposal_id' in args &&
        typeof (args as { proposal_id?: unknown }).proposal_id === 'string'
          ? (args as { proposal_id: string }).proposal_id
          : null
      if (id) ids.push(id)
    }
    return ids
  }, [thinkingNotes])

  return (
    <>
      {proposalIds.map((id) => (
        <ProposalCard key={id} proposalId={id} />
      ))}
      {hasThoughts &&
        thinkingTimeSeconds !== null &&
        thinkingTimeSeconds !== undefined && (
          <CollapsibleContainer>
            <CollapsibleHeader
              onClick={() => setIsThoughtsOpen(!isThoughtsOpen)}
              aria-expanded={isThoughtsOpen}>
              <ChevronIcon $isExpanded={isThoughtsOpen}>
                <ChevronDown size={12} />
              </ChevronIcon>
              Thought for {formatThinkingTime(thinkingTimeSeconds)}
            </CollapsibleHeader>
            <CollapsibleContent $isExpanded={isThoughtsOpen}>
              {thinkingNotes?.map((note, idx) => {
                const { documents, instruments } = splitSourcesByType(
                  note.sources
                )
                const instrumentLabel =
                  instruments.length > 0
                    ? formatInstrumentGroupLabel(instruments.map((i) => i.text))
                    : ''
                const instrumentEntryIds = instruments.map(
                  (i) => i.data.entryId as string
                )
                const instrumentStart = instruments
                  .map((i) => i.data.startDatetime as string | undefined)
                  .filter(Boolean)
                  .sort()[0]
                const instrumentEnd = instruments
                  .map((i) => i.data.endDatetime as string | undefined)
                  .filter(Boolean)
                  .sort()
                  .pop()

                return (
                  <React.Fragment key={idx}>
                    {note.text && (
                      <ThoughtItemContainer>
                        <ThoughtText>{note.text}</ThoughtText>
                        <ThoughtFeedback>
                          <FeedbackMenu
                            target={{
                              key: buildThoughtFeedbackKey(
                                note.id,
                                messageId,
                                messageIndex,
                                idx
                              ),
                              thoughtId: note.id,
                              messageId,
                              messageIndex,
                              thoughtIndex: idx,
                            }}
                          />
                        </ThoughtFeedback>
                      </ThoughtItemContainer>
                    )}
                    {documents.map((source) => (
                      <ReferenceItem key={`thinking-doc-${source.id}`}>
                        <ReferenceContent
                          onClick={() => handleOpenDocumentInNewTab(source)}>
                          <FileText />
                          <ReferenceTitle>{source.text}</ReferenceTitle>
                        </ReferenceContent>
                        <OpenInNewTabButton
                          onClick={() => handleOpenDocumentInNewTab(source)}>
                          <Plus />
                        </OpenInNewTabButton>
                      </ReferenceItem>
                    ))}
                    {instruments.length > 0 && (
                      <ReferenceItem key={`thinking-inst-${idx}`}>
                        <ReferenceContent
                          onClick={() =>
                            handleOpenInstrumentInNewTab(
                              instrumentEntryIds,
                              instrumentStart,
                              instrumentEnd
                            )
                          }>
                          <LineChart />
                          <ReferenceTitle>{instrumentLabel}</ReferenceTitle>
                        </ReferenceContent>
                        <OpenInNewTabButton
                          onClick={() =>
                            handleOpenInstrumentInNewTab(
                              instrumentEntryIds,
                              instrumentStart,
                              instrumentEnd
                            )
                          }>
                          <Plus />
                        </OpenInNewTabButton>
                      </ReferenceItem>
                    )}
                  </React.Fragment>
                )
              })}
            </CollapsibleContent>
          </CollapsibleContainer>
        )}

      {hasReferences && (
        <CollapsibleContainer>
          <CollapsibleHeader
            onClick={() => setIsReferencesOpen(!isReferencesOpen)}
            aria-expanded={isReferencesOpen}>
            <ChevronIcon $isExpanded={isReferencesOpen}>
              <ChevronDown size={12} />
            </ChevronIcon>
            <FileText />
            References ({unifiedReferences.length})
          </CollapsibleHeader>
          <CollapsibleContent $isExpanded={isReferencesOpen}>
            {unifiedReferences.map((ref) => {
              if (ref.type === 'document') {
                const refKey = `doc-${ref.source.id}`
                const tabMatch = referenceTabMap.get(refKey)
                return (
                  <ReferenceItem key={refKey}>
                    <ReferenceContent
                      onClick={() => {
                        if (tabMatch) {
                          documentUXContext?.setActiveTabId(tabMatch.tabId)
                        } else {
                          handleOpenDocumentInNewTab(ref.source)
                        }
                      }}>
                      <FileText />
                      <ReferenceTitle>{ref.source.text}</ReferenceTitle>
                    </ReferenceContent>
                    {tabMatch ? (
                      <Tooltip
                        title='Go to tab'
                        placement='top'
                        color={TOOLTIP_BG}
                        arrow={false}
                        styles={{ container: openTooltipStyle }}>
                        <TabColorDot
                          $color={tabMatch.color}
                          onClick={(e) => {
                            e.stopPropagation()
                            documentUXContext?.setActiveTabId(tabMatch.tabId)
                          }}
                        />
                      </Tooltip>
                    ) : (
                      <Tooltip
                        title='Open'
                        placement='top'
                        color={TOOLTIP_BG}
                        arrow={false}
                        styles={{ container: openTooltipStyle }}>
                        <OpenInNewTabButton
                          onClick={() =>
                            handleOpenDocumentInNewTab(ref.source)
                          }>
                          <Plus />
                        </OpenInNewTabButton>
                      </Tooltip>
                    )}
                  </ReferenceItem>
                )
              }

              const refKey = 'inst-group'
              const tabMatch = referenceTabMap.get(refKey)
              return (
                <ReferenceItem key={refKey}>
                  <ReferenceContent
                    onClick={() => {
                      if (tabMatch) {
                        documentUXContext?.setActiveTabId(tabMatch.tabId)
                      } else {
                        handleOpenInstrumentInNewTab(
                          ref.entryIds,
                          ref.startDatetime,
                          ref.endDatetime
                        )
                      }
                    }}>
                    <LineChart />
                    <ReferenceTitle>{ref.label}</ReferenceTitle>
                  </ReferenceContent>
                  {tabMatch ? (
                    <Tooltip
                      title='Go to tab'
                      placement='top'
                      color={TOOLTIP_BG}
                      arrow={false}
                      styles={{ container: openTooltipStyle }}>
                      <TabColorDot
                        $color={tabMatch.color}
                        onClick={(e) => {
                          e.stopPropagation()
                          documentUXContext?.setActiveTabId(tabMatch.tabId)
                        }}
                      />
                    </Tooltip>
                  ) : existingDashboardTabs.length > 0 ? (
                    <Dropdown
                      menu={{
                        items: buildInstrumentDropdownMenu(
                          ref.entryIds,
                          ref.startDatetime,
                          ref.endDatetime
                        ),
                      }}
                      trigger={['click']}
                      placement='bottomRight'>
                      <Tooltip
                        title='Open'
                        placement='top'
                        color={TOOLTIP_BG}
                        arrow={false}
                        styles={{ container: openTooltipStyle }}>
                        <OpenInNewTabButton
                          onClick={(e) => e.stopPropagation()}>
                          <Plus />
                        </OpenInNewTabButton>
                      </Tooltip>
                    </Dropdown>
                  ) : (
                    <Tooltip
                      title='Open'
                      placement='top'
                      color={TOOLTIP_BG}
                      arrow={false}
                      styles={{ container: openTooltipStyle }}>
                      <OpenInNewTabButton
                        onClick={() =>
                          handleOpenInstrumentInNewTab(
                            ref.entryIds,
                            ref.startDatetime,
                            ref.endDatetime
                          )
                        }>
                        <Plus />
                      </OpenInNewTabButton>
                    </Tooltip>
                  )}
                </ReferenceItem>
              )
            })}
          </CollapsibleContent>
        </CollapsibleContainer>
      )}
    </>
  )
}
